// How an answer reads (ERP-135): Markdown, with two kinds of fenced block the assistant is told to
// write — ```chart (JSON: a bar, line or pie chart of figures it looked up) and ```followups (JSON:
// questions to ask next, shown as buttons). A block that doesn't parse shows as what it is.
import { t } from "@keel/core";
import { Markdown } from "@keel/ui";
import { Fragment, useMemo } from "react";
import { type ChartSpec, split } from "./parts";

export function Answer({
  text,
  last,
  onAsk,
}: {
  text: string;
  /** Only the latest answer offers its follow-ups. */
  last: boolean;
  onAsk: (q: string) => void;
}) {
  const parts = useMemo(() => split(text), [text]);
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {p.kind === "md" ? (
            <Markdown source={p.text} />
          ) : p.kind === "chart" ? (
            <Chart spec={p.spec} />
          ) : last ? (
            <div className="assistant-followups">
              {p.items.map((q) => (
                <button key={q} type="button" className="chip" onClick={() => onAsk(q)}>
                  {q}
                </button>
              ))}
            </div>
          ) : null}
        </Fragment>
      ))}
    </>
  );
}

const COLORS = ["var(--accent)", "var(--teal)", "var(--info)", "var(--warn)", "var(--ok)", "var(--danger)"];
const fmt = (n: number) =>
  Math.abs(n) >= 1000
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** A small chart, drawn here: no chart library for three shapes. */
export function Chart({ spec }: { spec: ChartSpec }) {
  const W = 520;
  const H = 220;
  const pad = { l: 56, r: 12, t: 12, b: 44 };
  const labels = spec.labels.slice(0, 40);
  const series = spec.series.slice(0, 6).map((s) => ({ ...s, values: s.values.slice(0, labels.length) }));
  const legend = series.length > 1 || spec.type === "pie";
  if (spec.type === "pie") {
    const values = (series[0]?.values ?? []).map((v) => Math.max(0, v));
    const total = values.reduce((a, b) => a + b, 0) || 1;
    let angle = -Math.PI / 2;
    const r = 80;
    const c = { x: 110, y: 100 };
    return (
      <figure className="assistant-chart">
        {spec.title && <figcaption>{spec.title}</figcaption>}
        <svg viewBox="0 0 520 200" role="img" aria-label={spec.title ?? t("Chart")}>
          {values.map((v, i) => {
            const a = (v / total) * Math.PI * 2;
            const [x1, y1] = [c.x + r * Math.cos(angle), c.y + r * Math.sin(angle)];
            angle += a;
            const [x2, y2] = [c.x + r * Math.cos(angle), c.y + r * Math.sin(angle)];
            const d =
              values.filter((x) => x > 0).length === 1
                ? `M ${c.x - r} ${c.y} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0`
                : `M ${c.x} ${c.y} L ${x1} ${y1} A ${r} ${r} 0 ${a > Math.PI ? 1 : 0} 1 ${x2} ${y2} Z`;
            return (
              <path key={i} d={d} fill={COLORS[i % COLORS.length]}>
                <title>{`${labels[i] ?? ""}: ${fmt(v)} (${Math.round((v / total) * 100)}%)`}</title>
              </path>
            );
          })}
          {labels.map((l, i) => (
            <g key={l} transform={`translate(230 ${20 + i * 20})`}>
              <rect width="10" height="10" y="-9" fill={COLORS[i % COLORS.length]} rx="2" />
              <text x="16" className="assistant-chart-text">
                {l} · {fmt(values[i] ?? 0)} ({Math.round(((values[i] ?? 0) / total) * 100)}%)
              </text>
            </g>
          ))}
        </svg>
      </figure>
    );
  }
  const all = series.flatMap((s) => s.values);
  const max = Math.max(0, ...all);
  const min = Math.min(0, ...all);
  const span = max - min || 1;
  const x0 = pad.l;
  const w = W - pad.l - pad.r;
  const h = H - pad.t - pad.b;
  const y = (v: number) => pad.t + h - ((v - min) / span) * h;
  const step = w / Math.max(1, labels.length);
  const ticks = [min, min + span / 2, max];
  const every = Math.ceil(labels.length / 10);
  return (
    <figure className="assistant-chart">
      {spec.title && <figcaption>{spec.title}</figcaption>}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={spec.title ?? t("Chart")}>
        {ticks.map((v) => (
          <g key={v}>
            <line x1={x0} x2={W - pad.r} y1={y(v)} y2={y(v)} className="assistant-chart-grid" />
            <text x={x0 - 6} y={y(v) + 4} textAnchor="end" className="assistant-chart-text">
              {fmt(v)}
            </text>
          </g>
        ))}
        {spec.type === "bar"
          ? series.map((s, si) =>
              s.values.map((v, i) => {
                const bw = (step * 0.75) / series.length;
                const bx = x0 + i * step + step * 0.125 + si * bw;
                return (
                  <rect
                    key={`${si}-${i}`}
                    x={bx}
                    width={Math.max(1, bw - 1)}
                    y={Math.min(y(v), y(0))}
                    height={Math.abs(y(v) - y(0))}
                    fill={COLORS[si % COLORS.length]}
                    rx="2"
                  >
                    <title>{`${labels[i] ?? ""}${s.name ? ` · ${s.name}` : ""}: ${fmt(v)}`}</title>
                  </rect>
                );
              }),
            )
          : series.map((s, si) => (
              <polyline
                key={si}
                fill="none"
                stroke={COLORS[si % COLORS.length]}
                strokeWidth="2"
                points={s.values.map((v, i) => `${x0 + i * step + step / 2},${y(v)}`).join(" ")}
              />
            ))}
        {labels.map((l, i) =>
          i % every === 0 ? (
            <text
              key={l}
              x={x0 + i * step + step / 2}
              y={H - pad.b + 16}
              textAnchor="middle"
              className="assistant-chart-text"
            >
              {l.length > 10 ? `${l.slice(0, 9)}…` : l}
            </text>
          ) : null,
        )}
      </svg>
      {legend && (
        <div className="assistant-chart-legend">
          {series.map((s, i) => (
            <span key={s.name ?? i}>
              <i style={{ background: COLORS[i % COLORS.length] }} />
              {s.name ?? ""}
            </span>
          ))}
        </div>
      )}
    </figure>
  );
}
